import Phaser from 'phaser';
import type { NodeType } from '../sim/types';
import { nodeStyle } from './palette';
import { SANS, UI, Panel } from './theme';
import { t } from '../i18n';

type LegendEntry = {
  type: NodeType;
  title: string;
  body: string;
};

const ENTRIES: LegendEntry[] = [
  { type: 'normal',    title: 'NODE',      body: 'Collapses on one pulse. Fires on all open wires.' },
  { type: 'required',  title: 'REQUIRED',  body: 'Must collapse for you to win. Some need 2+ pulses.' },
  { type: 'protected', title: 'PROTECTED', body: 'If a pulse hits one, the run fails instantly.' },
  { type: 'relay',     title: 'RELAY',     body: 'When it collapses, it opens its locked wires.' },
  { type: 'shield',    title: 'SHIELD',    body: 'Protects another node. Drop the shield first.' },
  { type: 'splitter',  title: 'SPLITTER',  body: 'Fires N pulses on every output wire at once.' },
  { type: 'capacitor', title: 'CAPACITOR', body: 'Stores a pulse and re-emits it a tick later.' },
  { type: 'core',      title: 'CORE',      body: 'High threshold + decay. Needs converging pulses.' },
];

const WIRE_HELP: { title: string; body: string }[] = [
  { title: 'NORMAL',      body: 'Bidirectional. Carries 1 pulse per tick.' },
  { title: 'DIRECTIONAL', body: 'Arrow shows the only direction it conducts.' },
  { title: 'DELAY',       body: 'Dashed gold. Pulse arrives 3 ticks later.' },
  { title: 'LOCKED',      body: 'Closed until a relay opens it.' },
  { title: 'FORBIDDEN',   body: 'Dashed red arrow. Any pulse on it = instant fail.' },
];

/**
 * A "?" button in the HUD that opens a full-screen panel listing every
 * node type in this level + every wire type variant, with a one-line
 * explanation each. The button is always available; the panel toggles.
 */
export class Legend {
  private scene: Phaser.Scene;
  private button: Phaser.GameObjects.Container;
  private panel?: Phaser.GameObjects.Container;
  private typesInLevel: Set<NodeType>;
  private open = false;
  private onPanelOpened?: (panel: Phaser.GameObjects.Container) => void;

  constructor(
    scene: Phaser.Scene,
    typesInLevel: NodeType[],
    onPanelOpened?: (panel: Phaser.GameObjects.Container) => void,
  ) {
    this.scene = scene;
    this.typesInLevel = new Set(typesInLevel);
    this.onPanelOpened = onPanelOpened;

    const { width } = scene.scale;
    const bx = width - 78;
    const by = 62;

    this.button = scene.add.container(bx, by).setDepth(920).setScrollFactor(0);
    const bg = scene.add.circle(0, 0, 33, 0x0a1024, 0.92)
      .setStrokeStyle(3, 0x6a8cff, 0.95);
    const q = scene.add.text(0, 0, '?', {
      fontFamily: SANS,
      fontSize: '36px',
      color: '#88a8ff',
      fontStyle: 'bold',
    }).setOrigin(0.5, 0.55);
    this.button.add([bg, q]);
    this.button.setSize(132, 132);
    // 132 design px is about 48 css px on a phone: the disc stays small, the target does not.
    this.button.setInteractive(new Phaser.Geom.Circle(0, 0, 66), Phaser.Geom.Circle.Contains);
    if (this.button.input) this.button.input.cursor = 'pointer';

    this.button.on('pointerover', () => {
      bg.setStrokeStyle(2, 0xaad0ff, 1);
      q.setColor('#eaf2ff');
    });
    this.button.on('pointerout', () => {
      bg.setStrokeStyle(2, 0x6a8cff, 0.95);
      q.setColor('#88a8ff');
    });
    this.button.on('pointerdown', () => this.toggle());
  }

  toggle() {
    if (this.open) this.close(); else this.show();
  }

  /**
   * Brief attention pulse on the "?" chip — used after a tutorial popup
   * dismisses to teach the player that the legend is always available.
   */
  pulseHint(scene: Phaser.Scene) {
    const ring = scene.add.circle(0, 0, 33, 0x88e8ff, 0)
      .setStrokeStyle(3, 0x88e8ff, 1)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.button.add(ring);
    scene.tweens.add({
      targets: ring,
      radius: 90,
      alpha: { from: 0.85, to: 0 },
      duration: 900,
      repeat: 1,
      ease: 'Cubic.easeOut',
      onComplete: () => ring.destroy(),
    });
    scene.tweens.add({
      targets: this.button,
      scale: { from: 1, to: 1.18 },
      duration: 220,
      yoyo: true,
      repeat: 2,
      ease: 'Sine.easeInOut',
    });
  }

  show() {
    if (this.open) return;
    this.open = true;
    const { width, height } = this.scene.scale;

    this.panel = this.scene.add.container(0, 0).setDepth(1500).setScrollFactor(0);

    // dim backdrop, dismiss on tap
    const dim = this.scene.add.rectangle(0, 0, width, height, 0x000000, 0.78)
      .setOrigin(0, 0)
      .setInteractive();
    dim.on('pointerdown', () => this.close());
    this.panel.add(dim);

    const cardW = Math.min(width - 96, 900);
    const cardX = width / 2;
    const cardY = height / 2;
    const visibleNodeEntries = ENTRIES.filter(e => this.typesInLevel.has(e.type));
    const nodePitch = 124, wirePitch = 112;
    const wantH = 150 + 64 + visibleNodeEntries.length * nodePitch + 64 + WIRE_HELP.length * wirePitch + 110;
    const cardH = Math.min(height - 120, wantH);
    // If the content outgrows the screen, tighten the row pitch instead of clipping.
    const fit = Math.min(1, (cardH - 150 - 64 - 64 - 110) / (visibleNodeEntries.length * nodePitch + WIRE_HELP.length * wirePitch));
    const card = new Panel(this.scene, cardX - cardW / 2, cardY - cardH / 2, cardW, cardH, {
      fill: 0x0b1226, fillAlpha: 0.99, stroke: UI.accent, strokeAlpha: 0.85,
    });
    this.panel.add(card.gfx);

    const left = cardX - cardW / 2 + 48;
    const headTop = cardY - cardH / 2 + 40;
    this.panel.add(this.scene.add.text(cardX, headTop, t('legend_title'), {
      fontFamily: SANS, fontSize: '46px', color: UI.text, fontStyle: '800',
    }).setOrigin(0.5, 0));

    let y = headTop + 90;
    const sectionHead = (label: string) => {
      this.panel!.add(this.scene.add.text(left, y, label, {
        fontFamily: SANS, fontSize: '24px', color: UI.textFaint, fontStyle: '800',
      }).setOrigin(0, 0));
      y += 50;
    };

    sectionHead(t('legend_nodes'));
    for (const entry of visibleNodeEntries) {
      this.drawRow(cardX, y, cardW, entry);
      y += nodePitch * fit;
    }

    y += 14;
    sectionHead(t('legend_wires'));
    for (const w of WIRE_HELP) {
      this.panel.add(this.scene.add.text(left, y, w.title, {
        fontFamily: SANS, fontSize: '28px', color: UI.text, fontStyle: '800',
      }).setOrigin(0, 0));
      this.panel.add(this.scene.add.text(left, y + 38, w.body, {
        fontFamily: SANS, fontSize: '25px', color: UI.textDim,
        wordWrap: { width: cardW - 96 },
      }).setOrigin(0, 0));
      y += wirePitch * fit;
    }

    this.panel.add(this.scene.add.text(cardX, cardY + cardH / 2 - 52, t('legend_close'), {
      fontFamily: SANS, fontSize: '26px', color: UI.textFaint, fontStyle: '700',
    }).setOrigin(0.5));

    // entrance tween
    this.panel.setAlpha(0);
    this.scene.tweens.add({
      targets: this.panel,
      alpha: 1,
      duration: 160,
      ease: 'Cubic.easeOut',
    });

    // Let GameScene re-route this panel to the UI camera so it doesn't zoom
    if (this.onPanelOpened) this.onPanelOpened(this.panel);
  }

  private drawRow(cardX: number, y: number, cardW: number, entry: LegendEntry) {
    const style = nodeStyle(entry.type);
    const iconX = cardX - cardW / 2 + 48 + 34;
    const bg = this.scene.add.circle(iconX, y + 32, 30, style.fill);
    const ring = this.scene.add.circle(iconX, y + 32, 30, undefined, 0)
      .setStrokeStyle(3, style.ring, 1);
    const aura = this.scene.add.circle(iconX, y + 32, 40, style.ring, 0.18)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.panel!.add([aura, bg, ring]);

    const title = this.scene.add.text(iconX + 62, y, entry.title, {
      fontFamily: SANS, fontSize: '28px', color: this.tagColor(entry.type), fontStyle: '800',
    }).setOrigin(0, 0);
    const body = this.scene.add.text(iconX + 62, y + 38, entry.body, {
      fontFamily: SANS, fontSize: '25px', color: UI.textDim,
      wordWrap: { width: cardW - 48 - 62 - 68 - 24 },
    }).setOrigin(0, 0);
    this.panel!.add(title);
    this.panel!.add(body);
  }

  private tagColor(t: NodeType): string {
    switch (t) {
      case 'required':  return '#6cf0ff';
      case 'protected': return '#ff7a99';
      case 'relay':     return '#ffd070';
      case 'shield':    return '#88ffb0';
      case 'splitter':  return '#ff99dd';
      case 'capacitor': return '#fff088';
      case 'core':      return '#dd99ff';
      default:          return '#cdd8f5';
    }
  }

  close() {
    if (!this.open || !this.panel) return;
    this.open = false;
    const p = this.panel;
    this.panel = undefined;
    this.scene.tweens.add({
      targets: p,
      alpha: 0,
      duration: 140,
      onComplete: () => p.destroy(),
    });
  }

  /** All UI-camera roots — used by GameScene to assign cameras correctly. */
  uiRoots(): Phaser.GameObjects.GameObject[] {
    const list: Phaser.GameObjects.GameObject[] = [this.button];
    if (this.panel) list.push(this.panel);
    return list;
  }

  destroy() {
    this.button.destroy();
    this.panel?.destroy();
  }
}
